// ═══ TOAST NOTIFICATION ═══

export function showToast(message, duration = 2500) {
  const existing = document.querySelector('.export-toast')
  if (existing) existing.remove()

  const toast = document.createElement('div')
  toast.className = 'export-toast'
  toast.textContent = message
  document.body.appendChild(toast)
  requestAnimationFrame(() => toast.classList.add('show'))
  setTimeout(() => {
    toast.classList.remove('show')
    setTimeout(() => toast.remove(), 300)
  }, duration)
}

// ═══ HELPERS ═══

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9_\-. ]/g, '').replace(/\s+/g, '_')
}

function buildFilename(label, ext) {
  const date = new Date().toISOString().slice(0, 10)
  return `${sanitizeFilename(label)}_${date}.${ext}`
}

/**
 * Clone an element and restyle it for print (black text, white background).
 * Returns { wrapper, cleanup }.
 */
function preparePrintClone(element) {
  const wrapper = document.createElement('div')
  wrapper.style.cssText = [
    'position:fixed', 'left:-9999px', 'top:0',
    'width:800px', 'background:#fff', 'color:#000',
    'padding:32px', 'font-family:Inter,Calibri,Arial,sans-serif',
    'font-size:13px', 'line-height:1.7',
  ].join(';')

  const clone = element.cloneNode(true)

  // Force all elements to print-friendly colors
  clone.style.color = '#000'
  clone.style.background = '#fff'
  clone.querySelectorAll('*').forEach(el => {
    const tag = el.tagName.toLowerCase()
    el.style.color = '#000'
    el.style.background = 'transparent'
    el.style.borderColor = '#ccc'
    el.style.textShadow = 'none'
    el.style.boxShadow = 'none'

    if (['h1','h2','h3','h4','h5','h6'].includes(tag)) {
      el.style.color = '#111'
      el.style.borderColor = '#ddd'
    }
    if (tag === 'th') {
      el.style.background = '#f0f0f0'
      el.style.color = '#333'
    }
    if (tag === 'code') {
      el.style.background = '#f5f5f5'
      el.style.color = '#333'
      el.style.borderColor = '#ddd'
    }
    if (tag === 'pre') {
      el.style.background = '#f8f8f8'
      el.style.color = '#333'
      el.style.borderColor = '#ddd'
    }
    if (tag === 'blockquote') {
      el.style.background = '#fafafa'
      el.style.color = '#444'
      el.style.borderColor = '#999'
    }
    if (tag === 'a') {
      el.style.color = '#1a0dab'
    }
    if (tag === 'strong' || tag === 'b') {
      el.style.color = '#000'
    }
  })

  wrapper.appendChild(clone)
  document.body.appendChild(wrapper)
  return { wrapper, cleanup: () => wrapper.remove() }
}

// ═══ CLIPBOARD ═══

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    showToast('Copied to clipboard!')
    return true
  } catch {
    showToast('Gagal copy ke clipboard')
    return false
  }
}

// ═══ PDF EXPORT (black text, white background) ═══

export async function exportAsPdf(element, label) {
  if (!element) { showToast('Tidak ada konten untuk di-export'); return }
  showToast('Generating PDF...')

  const { wrapper, cleanup } = preparePrintClone(element)

  try {
    const html2pdf = (await import('html2pdf.js')).default
    await html2pdf().set({
      margin: [10, 10, 10, 10],
      filename: buildFilename(label, 'pdf'),
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollY: 0,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(wrapper).save()
    showToast(`PDF berhasil di-download`)
  } catch {
    showToast('PDF export gagal')
  } finally {
    cleanup()
  }
}

// ═══ DOCX EXPORT (Word-compatible HTML, black text) ═══

export function exportAsDocx(element, label) {
  if (!element) { showToast('Tidak ada konten untuk di-export'); return }
  showToast('Generating DOCX...')

  const clone = element.cloneNode(true)

  // Strip dark-theme inline styles from clone
  clone.querySelectorAll('*').forEach(el => {
    el.removeAttribute('style')
    el.removeAttribute('class')
  })
  clone.removeAttribute('style')
  clone.removeAttribute('class')

  const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <style>
    body {
      font-family: Calibri, Arial, sans-serif;
      color: #000;
      background: #fff;
      font-size: 11pt;
      line-height: 1.6;
      padding: 20px;
    }
    h1 { font-size: 18pt; font-weight: bold; color: #111; margin: 16px 0 8px; }
    h2 { font-size: 15pt; font-weight: bold; color: #111; margin: 14px 0 6px; }
    h3 { font-size: 13pt; font-weight: bold; color: #222; margin: 12px 0 4px; }
    h4, h5, h6 { font-size: 11pt; font-weight: bold; color: #333; margin: 10px 0 4px; }
    p { margin: 0 0 8px; }
    ul, ol { margin: 0 0 8px; padding-left: 24px; }
    li { margin-bottom: 4px; }
    table { border-collapse: collapse; width: 100%; margin: 8px 0; }
    th { background: #f0f0f0; font-weight: bold; border: 1px solid #bbb; padding: 6px 10px; text-align: left; }
    td { border: 1px solid #ccc; padding: 6px 10px; }
    blockquote { border-left: 3px solid #999; padding: 8px 12px; margin: 8px 0; color: #444; background: #fafafa; }
    code { font-family: Consolas, monospace; font-size: 10pt; background: #f5f5f5; padding: 1px 4px; border: 1px solid #ddd; }
    pre { font-family: Consolas, monospace; font-size: 10pt; background: #f8f8f8; padding: 12px; border: 1px solid #ddd; white-space: pre-wrap; }
    a { color: #1a0dab; }
    strong { color: #000; }
    img { max-width: 100%; }
  </style>
</head>
<body>
${clone.innerHTML}
</body>
</html>`

  const blob = new Blob(['\ufeff', html], { type: 'application/msword' })
  downloadBlob(blob, buildFilename(label, 'doc'))
  showToast('DOCX berhasil di-download')
}

// ═══ PNG EXPORT ═══

export async function exportAsPng(element, label) {
  if (!element) { showToast('Tidak ada konten untuk di-export'); return }
  showToast('Generating PNG...')

  const { wrapper, cleanup } = preparePrintClone(element)

  try {
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    })
    canvas.toBlob(blob => {
      if (blob) {
        downloadBlob(blob, buildFilename(label, 'png'))
        showToast('PNG berhasil di-download')
      }
    }, 'image/png')
  } catch {
    showToast('PNG export gagal')
  } finally {
    cleanup()
  }
}

// ═══ TXT EXPORT ═══

export function exportAsTxt(text, label) {
  if (!text) { showToast('Tidak ada konten untuk di-export'); return }
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  downloadBlob(blob, buildFilename(label, 'txt'))
  showToast('TXT berhasil di-download')
}

// ═══ MARKDOWN EXPORT ═══

export function exportAsMarkdown(md, label) {
  if (!md) { showToast('Tidak ada konten untuk di-export'); return }
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
  downloadBlob(blob, buildFilename(label, 'md'))
  showToast('Markdown berhasil di-download')
}

// ═══ CSV EXPORT ═══

export function exportAsCsv(data, label) {
  if (!data) { showToast('Tidak ada data untuk di-export'); return }
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, buildFilename(label, 'csv'))
  showToast('CSV berhasil di-download')
}

// ═══ JSON EXPORT ═══

export function exportAsJson(data, label) {
  if (!data) { showToast('Tidak ada data untuk di-export'); return }
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' })
  downloadBlob(blob, buildFilename(label, 'json'))
  showToast('JSON berhasil di-download')
}

// ═══ SHARE TO WHATSAPP ═══

export function shareToWhatsApp(text) {
  if (!text) { showToast('Tidak ada konten untuk di-share'); return }
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`
  window.open(url, '_blank')
}

// ═══ SHARE VIA EMAIL ═══

export function shareViaEmail(subject, body) {
  const mailto = `mailto:?subject=${encodeURIComponent(subject || 'Report')}&body=${encodeURIComponent(body || '')}`
  window.open(mailto)
}

// ═══ BUILD SHARE TEXT (for clipboard) ═══

export function buildShareText({ title, date, summary, todos }) {
  const lines = []
  lines.push(title)
  if (date) lines.push(date)
  lines.push('')

  if (summary) {
    lines.push('Ringkasan:')
    lines.push(summary)
    lines.push('')
  }

  if (todos && todos.length) {
    const done = todos.filter(t => t.done).length
    lines.push(`Rekomendasi (${done}/${todos.length} done):`)
    todos.forEach(t => lines.push(`  ${t.done ? '[v]' : '[ ]'} ${t.text}`))
    lines.push('')
  }

  lines.push('— Uprint.id Dashboard')
  return lines.join('\n')
}

// ═══ STRIP MARKDOWN TO PLAIN TEXT ═══

export function stripMarkdown(md) {
  if (!md) return ''
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/^[-*+]\s+/gm, '- ')
    .replace(/^>\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
